import { portionSizeToRange, valueToCompartments } from '../config/guidanceQuestions';

export function parseDimensions(dimStr) {
  if (!dimStr) return { length: null, width: null, height: null };
  const nums = (dimStr.match(/[\d.]+/g) || []).map(Number);
  if (nums.length === 2) {
    return { length: nums[0] || null, width: nums[1] || null, height: null };
  }
  return {
    length: nums[0] || null,
    width: nums[1] || null,
    height: nums[2] || null
  };
}

export function parseVolumeOz(volStr) {
  if (!volStr) return null;
  const match = volStr.match(/[\d.]+/);
  return match ? Number(match[0]) : null;
}

export function footprintKey(dimStr) {
  const { length, width } = parseDimensions(dimStr);
  if (!length || !width) return null;
  const normalize = (value) => {
    if (value == null) return null;
    if (value % 1 === 0) return String(value);
    return value
      .toFixed(2)
      .replace(/0+$/, '')
      .replace(/\.$/, '');
  };

  const normalizedLength = normalize(length);
  const normalizedWidth = normalize(width);
  if (!normalizedLength || !normalizedWidth) return null;
  return `${normalizedLength}×${normalizedWidth}`;
}

export function depthBucket(height) {
  if (height == null) return 'Unknown';
  if (height <= 1.5) return 'Shallow';
  if (height <= 2.1) return 'Mid';
  if (height <= 3) return 'Deep';
  return 'Extra-Deep';
}

export function volumeBucket(oz) {
  if (oz == null) return 'Unknown';
  if (oz < 16) return 'Small';
  if (oz <= 40) return 'Medium';
  if (oz <= 80) return 'Large';
  return 'Family';
}

export function mapMaterial(matRaw) {
  if (!matRaw) return 'Unknown';
  const normalized = String(matRaw).toUpperCase();
  if (normalized.includes('CPET')) return 'CPET';
  if (normalized.includes('MFPP') || normalized === 'PP') return 'PP/MFPP';
  if (normalized.includes('PET')) return 'PET';
  return 'Unknown';
}

export const knownFootprintOrder = [
  '8.5×6.5',
  '8.5×6.38',
  '10.25×8.125',
  '10.25×8',
  '10.79×6.79',
  '12.44×10.37',
  '12.46×10.35',
  '6.67×5.04',
  '6.3×',
  '6.25×',
  '4.5×4.5'
];

export const ACCESSORY_GROUP_KEY = 'Lids & Accessories';

export const createDefaultContainerFilters = () => ({
  compartments: new Set(),
  footprints: new Set(),
  depthBuckets: new Set(),
  volumeBuckets: new Set(),
  materials: new Set(),
  brand: 'All'
});

export function normalizeMealContainers(mealContainers) {
  return mealContainers.map((item) => {
    const { length, width, height } = parseDimensions(item.dimensions);
    const volumeOz = parseVolumeOz(item.volume);
    const footprint = footprintKey(item.dimensions);
    const depthCategory = depthBucket(height);
    const volumeCategory = volumeBucket(volumeOz);
    const materialNormalized = mapMaterial(item.material);
    const brandNormalized = item.brand
      ? item.brand.includes('D&W')
        ? 'D&W Fine Pack'
        : item.brand
      : 'Teinnovations';
    const idString = String(item.id);

    return {
      ...item,
      idString,
      length,
      width,
      height,
      footprint,
      volumeOz,
      depthBucket: depthCategory,
      volumeBucket: volumeCategory,
      materialMapped: materialNormalized,
      isAccessory: item.compartments === null,
      brandNormalized,
      footprintLabel: footprint ? `${footprint} Family` : 'Other Footprints'
    };
  });
}

const DELI_PRODUCT_TYPES = new Set(['dipsSpreads', 'soupsSauces', 'freshSalads']);
const MULTI_COMPARTMENT_VALUES = new Set(['two', 'threePlus']);

export const GUIDANCE_CONTAINER_TYPES = {
  MEAL: 'meal',
  DELI: 'deli'
};

const ensureRange = (range) => {
  if (!range) return null;
  const { min = 0, max = Number.POSITIVE_INFINITY } = range;
  return { min, max };
};

const deriveCompartmentsRange = (compartmentValue) => {
  const range = valueToCompartments[compartmentValue];
  if (!range) return null;
  return { min: range.min, max: range.max };
};

export function deriveContainerTypeFromAnswers({ productType, compartments, useCase }) {
  if (!productType && !compartments && !useCase) {
    return GUIDANCE_CONTAINER_TYPES.MEAL;
  }

  if (MULTI_COMPARTMENT_VALUES.has(compartments)) {
    return GUIDANCE_CONTAINER_TYPES.MEAL;
  }

  if (useCase === 'oven' || useCase === 'multiple') {
    return GUIDANCE_CONTAINER_TYPES.MEAL;
  }

  if (productType && DELI_PRODUCT_TYPES.has(productType)) {
    return GUIDANCE_CONTAINER_TYPES.DELI;
  }

  return GUIDANCE_CONTAINER_TYPES.MEAL;
}

const scoreVolumeProximity = (itemVolume, targetRange) => {
  if (!targetRange) return 0;
  if (itemVolume == null) return Number.POSITIVE_INFINITY;
  const { min, max } = targetRange;
  if (max === Number.POSITIVE_INFINITY) {
    const baseline = Math.max(min, 32);
    return Math.abs(itemVolume - baseline);
  }
  const midpoint = (min + max) / 2;
  return Math.abs(itemVolume - midpoint);
};

const filterByCompartments = (item, compartmentRange, containerType) => {
  if (!compartmentRange) return true;
  if (containerType === GUIDANCE_CONTAINER_TYPES.DELI) {
    const deliCompartments = Number(item['Compartments'] || 1);
    if (Number.isNaN(deliCompartments)) return true;
    return deliCompartments >= compartmentRange.min && deliCompartments <= compartmentRange.max;
  }

  const { compartments } = item;
  if (compartments == null) return compartmentRange.min <= 1;
  return compartments >= compartmentRange.min && compartments <= compartmentRange.max;
};

export function matchMealContainersForGuidance(normalizedContainers, { portionSize, compartments }) {
  const portionRange = ensureRange(portionSizeToRange[portionSize]);
  const compartmentRange = deriveCompartmentsRange(compartments);

  const filtered = normalizedContainers
    .filter((item) => !item.isAccessory)
    .filter((item) => {
      if (!portionRange || item.volumeOz == null) return true;
      const { min, max } = portionRange;
      if (max === Number.POSITIVE_INFINITY) {
        return item.volumeOz >= min;
      }
      return item.volumeOz >= min && item.volumeOz <= max;
    })
    .filter((item) => filterByCompartments(item, compartmentRange, GUIDANCE_CONTAINER_TYPES.MEAL));

  const sorted = filtered.sort((a, b) => {
    const aScore = scoreVolumeProximity(a.volumeOz, portionRange);
    const bScore = scoreVolumeProximity(b.volumeOz, portionRange);
    if (aScore === bScore) {
      return (a.collection || '').localeCompare(b.collection || '');
    }
    return aScore - bScore;
  });

  const grouped = new Map();
  sorted.forEach((item) => {
    const key = item.collection || 'Meal Trays';
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key).push(item);
  });

  const groups = Array.from(grouped.entries()).map(([collection, items]) => ({
    collection,
    items: items.slice(0, 4)
  }));

  return groups.slice(0, 5);
}

export function matchDeliContainersForGuidance(deliContainers, { portionSize, compartments }) {
  const portionRange = ensureRange(portionSizeToRange[portionSize]);
  const compartmentRange = deriveCompartmentsRange(compartments);

  const filtered = deliContainers
    .filter((item) => {
      if (!portionRange) return true;
      const oz = Number(item['Fluid oz']);
      if (Number.isNaN(oz)) return true;
      const { min, max } = portionRange;
      if (max === Number.POSITIVE_INFINITY) {
        return oz >= min;
      }
      return oz >= min && oz <= max;
    })
    .filter((item) => filterByCompartments(item, compartmentRange, GUIDANCE_CONTAINER_TYPES.DELI));

  const sorted = filtered.sort((a, b) => {
    const aOz = Number(a['Fluid oz']);
    const bOz = Number(b['Fluid oz']);
    const aScore = scoreVolumeProximity(aOz, portionRange);
    const bScore = scoreVolumeProximity(bOz, portionRange);
    if (aScore === bScore) {
      return (a.Description || '').localeCompare(b.Description || '');
    }
    return aScore - bScore;
  });

  return sorted.slice(0, 6);
}

export function matchContainersForGuidance({ normalizedContainers, deliContainers, answers }) {
  const containerType = deriveContainerTypeFromAnswers(answers);

  if (containerType === GUIDANCE_CONTAINER_TYPES.DELI) {
    return {
      containerType,
      groups: [
        {
          collection: 'Deli Containers',
          items: matchDeliContainersForGuidance(deliContainers, answers)
        }
      ]
    };
  }

  return {
    containerType,
    groups: matchMealContainersForGuidance(normalizedContainers, answers)
  };
}
